import { COLOR_FLYWEIGHT, formatData } from '@utilities';

import { addSlideFooter } from '../slide_footer';
import { parseRichTextToPptRuns } from '../helpers.js';
import { FOOTER_TEXT, TABLE_BORDER, TABLE_FONT_SIZE, TEXT_COLOR, TITLE_TEXT } from '../constants';

/**
 * Creates multiple continuous sum table slides for CONT_SUM type questions
 * One slide per quota group when compareBy is quota_group, or split tables for overall
 * @param {Object} options
 * @param {string} options.compareBy - Compare by object with value and label properties
 * @param {string} options.displayAs - Display format (PERCENT or COUNT)
 * @param {Object} options.pptx
 * @param {Object} options.questionData - CONT_SUM question data with table structure
 * @param {string} options.questionNumber
 * @param {string} options.questionTitle
 * @param {string} options.reportType
 * @param {string} options.reportUrl
 * @param {string} options.title
 * @returns {void}
 */
export const createContSumChartSlide = ({
  compareBy,
  displayAs,
  pptx,
  questionData,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  title,
}) => {
  const questionTitleRuns = parseRichTextToPptRuns(questionTitle);
  if (!questionTitleRuns.length) return;

  // Determine which table to use based on displayAs
  const isDisplayAsPercent = displayAs === 'pct';
  const tableToUse = isDisplayAsPercent ? questionData.tablePct : questionData.tableCount;

  // Check if we're comparing by quota groups
  if (compareBy?.value === 'quota_group') {
    // Create one slide per quota group
    createQuotaGroupSlides({
      pptx,
      questionNumber,
      questionTitle,
      reportType,
      reportUrl,
      tableToUse,
      title,
    });
  } else {
    // Handle overall and demographic filter comparisons
    createOverallSlides({
      pptx,
      questionNumber,
      questionTitle,
      reportType,
      reportUrl,
      tableToUse,
      title,
    });
  }
};

/**
 * Creates separate slides for each segment (quota group or other compare by breakout)
 * @param {Object} options
 * @param {Object} options.pptx
 * @param {string} options.questionNumber
 * @param {string} options.questionTitle
 * @param {string} options.reportType
 * @param {string} options.reportUrl
 * @param {Object} options.tableToUse
 * @param {string} options.title
 */
const createQuotaGroupSlides = ({
  pptx,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  tableToUse,
  title,
}) => {
  const dataByGroup = {};

  tableToUse.data.forEach((row) => {
    const groupName = row.compare_by_breakout;
    if (!dataByGroup[groupName]) {
      dataByGroup[groupName] = [];
    }
    dataByGroup[groupName].push(row);
  });

  Object.entries(dataByGroup).forEach(([groupName, groupData]) => {
    const slideSuffix = ` Compared by: ${groupName}`;

    const groupTable = {
      columns: tableToUse.columns.filter((col) => {
        const accessor = col.accessor || col.accessorKey;
        return accessor !== 'compare_by_breakout';
      }),
      data: groupData,
    };

    createCustomTableSlide({
      pptx,
      questionNumber,
      questionTitle,
      reportType,
      reportUrl,
      slideSuffix,
      table: groupTable,
      title,
    });
  });
};

/**
 * Creates slides for overall data, splitting tables if they're too large
 * @param {Object} options
 * @param {string} options.questionNumber
 * @param {string} options.questionTitle
 * @param {Object} options.pptx
 * @param {string} options.reportType
 * @param {string} options.reportUrl
 * @param {Object} options.tableToUse
 * @param {string} options.title
 */
const createOverallSlides = ({
  pptx,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  tableToUse,
  title,
}) => {
  const optionGroups = {};

  tableToUse.data.forEach((row) => {
    const optionValue = row.option_value;
    if (!optionGroups[optionValue]) {
      optionGroups[optionValue] = [];
    }
    optionGroups[optionValue].push(row);
  });

  const uniqueOptions = Object.keys(optionGroups);
  const maxOptionsPerSlide = 10;

  const optionChunks = [];
  for (let i = 0; i < uniqueOptions.length; i += maxOptionsPerSlide) {
    optionChunks.push(uniqueOptions.slice(i, i + maxOptionsPerSlide));
  }

  optionChunks.forEach((optionChunk, chunkIndex) => {
    const chunkData = [];

    optionChunk.forEach((optionValue) => {
      chunkData.push(...optionGroups[optionValue]);
    });

    const slideSuffix =
      optionChunks.length > 1 ? ` (${chunkIndex + 1}/${optionChunks.length})` : '';

    const chunkTable = {
      columns: tableToUse.columns,
      data: chunkData,
    };

    createCustomTableSlide({
      pptx,
      questionNumber,
      questionTitle,
      reportType,
      reportUrl,
      slideSuffix,
      table: chunkTable,
      title,
    });
  });
};

/**
 * Processes table data to apply rowspan for identical values in the first column
 * @param {Array} data - Raw table data
 * @param {Array} columns - Column definitions
 * @returns {Array} - Processed table rows with rowspan applied
 */
const processTableDataWithRowspan = (data, columns) => {
  if (!data.length || !columns.length) return [];

  const processedRows = [];
  const firstColumnAccessor = columns[0].accessor || columns[0].accessorKey;
  let currentGroup = null;
  let groupStartIndex = 0;

  data.forEach((row) => {
    const firstColumnValue = formatData({
      format: columns[0].format,
      value: row[firstColumnAccessor],
    });

    const isNewGroup = currentGroup !== firstColumnValue;

    if (isNewGroup) {
      // Finalize the previous group by setting rowspan
      if (currentGroup !== null && processedRows.length > groupStartIndex) {
        const groupSize = processedRows.length - groupStartIndex;
        if (groupSize > 1) {
          // Set rowspan for the first cell of the group
          processedRows[groupStartIndex][0].options.rowspan = groupSize;
          // Remove the first cell from subsequent rows in the group
          for (let i = groupStartIndex + 1; i < processedRows.length; i++) {
            processedRows[i].shift();
          }
        }
      }

      // Start new group
      currentGroup = firstColumnValue;
      groupStartIndex = processedRows.length;
    }

    // Create the row data - always include all columns initially
    const rowData = columns.map((col, colIndex) => {
      const accessor = col.accessor || col.accessorKey;
      return {
        options: {
          align: colIndex === 0 ? 'left' : 'right',
          border: TABLE_BORDER,
          color: TEXT_COLOR,
          fontFace: 'Arial',
          fontSize: TABLE_FONT_SIZE,
          valign: 'middle',
        },
        text: formatData({ format: col.format, value: row[accessor] }),
      };
    });

    processedRows.push(rowData);
  });

  if (currentGroup !== null && processedRows.length > groupStartIndex) {
    const groupSize = processedRows.length - groupStartIndex;
    if (groupSize > 1) {
      // Set rowspan for the first cell of the final group
      processedRows[groupStartIndex][0].options.rowspan = groupSize;
      // Remove the first cell from subsequent rows in the final group
      for (let i = groupStartIndex + 1; i < processedRows.length; i++) {
        processedRows[i].shift();
      }
    }
  }

  return processedRows;
};

/**
 * Creates a custom table slide with proper footer including source hyperlink
 * @param {Object} options
 * @param {Object} options.pptx
 * @param {string} options.questionNumber
 * @param {string} options.questionTitle
 * @param {string} options.reportType
 * @param {string} options.reportUrl
 * @param {string} options.slideSuffix
 * @param {Object} options.table
 * @param {string} options.title
 */
const createCustomTableSlide = ({
  pptx,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  slideSuffix,
  table,
  title,
}) => {
  if (!table || !table.columns || !table.data) return;

  const { columns, data } = table;
  const headers = columns.map((col) => col.header || col.Header || col.accessor);
  const rowsPerSlide = 9;

  const dataChunks = [];
  for (let i = 0; i < data.length; i += rowsPerSlide) {
    dataChunks.push(data.slice(i, i + rowsPerSlide));
  }

  dataChunks.forEach((chunkData, chunkIndex) => {
    const slide = pptx.addSlide();

    const titleRuns = [
      ...(questionNumber ? [{ text: `Q${questionNumber}. ` }] : []),
      ...parseRichTextToPptRuns(questionTitle),
      ...(slideSuffix ? [{ text: slideSuffix }] : []),
      ...(dataChunks.length > 1 ? [{ text: ` (${chunkIndex + 1}/${dataChunks.length})` }] : []),
    ];

    const charsPerLine = 100;
    const estimatedLinesTitleText = `${questionNumber ? `Q${questionNumber}. ` : ''}${questionTitle}${slideSuffix || ''}`;
    const estimatedLines = Math.ceil(estimatedLinesTitleText.length / charsPerLine);
    const lineHeight = 0.25;
    const titleHeight = Math.max(estimatedLines * lineHeight, 0.5);

    const tableY = 0.5 + titleHeight + 0.3;

    slide.addText(titleRuns, {
      ...TITLE_TEXT,
      fontSize: 14,
    });

    const processedTableData = processTableDataWithRowspan(chunkData, columns);

    // Calculate column widths
    const TOTAL_TABLE_WIDTH = 9.3;
    const FIRST_COLUMN_RATIO = 0.25; // Give more space to first column for option names
    const columnCount = headers.length;
    const firstColumnWidth = TOTAL_TABLE_WIDTH * FIRST_COLUMN_RATIO;
    const remainingColumnWidth = (TOTAL_TABLE_WIDTH - firstColumnWidth) / (columnCount - 1);
    const columnWidths = [firstColumnWidth, ...Array(columnCount - 1).fill(remainingColumnWidth)];

    // Add table
    slide.addTable(
      [
        // Header row
        headers.map((header, colIndex) => ({
          options: {
            align: colIndex === 0 ? 'left' : 'right',
            bold: true,
            border: TABLE_BORDER,
            color: TEXT_COLOR,
            fill: { color: COLOR_FLYWEIGHT },
            fontFace: 'Arial',
            fontSize: TABLE_FONT_SIZE,
            valign: 'middle',
          },
          text: header,
        })),
        // Data rows with rowspan
        ...processedTableData,
      ],
      {
        border: TABLE_BORDER,
        colW: columnWidths,
        w: '95%',
        x: 0.3,
        y: tableY,
      }
    );

    // Add slide footer
    addSlideFooter({ pptx, reportType, reportUrl, slide });

    // Add source hyperlink
    slide.addText(
      [{ text: 'Source: ' }, { options: { hyperlink: { url: reportUrl } }, text: title }],
      FOOTER_TEXT
    );
  });
};
